import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  adminBinderTemplates,
  adminEisf,
  type ApprovalMode,
  type DocumentTypeApproverInput,
  type DocumentTypeWithApprovers,
} from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { FolderApproverPicker, newApproverRow } from './FolderApproverPicker';

const ESIGNATURE_WORKFLOW = 'Send for eSignature Approval';

export function parseWorkflows(json: string | null | undefined): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function approvalSummary(type: DocumentTypeWithApprovers | undefined): {
  enabled: boolean;
  label: string;
} {
  if (!type) return { enabled: false, label: 'No approvers' };
  const on = parseWorkflows(type.draft_state_workflows_json).includes(ESIGNATURE_WORKFLOW);
  const n = type.approvers.length;
  if (!on && n === 0) return { enabled: false, label: 'No approvers' };
  if (!on) return { enabled: false, label: `Off · ${n} approver${n === 1 ? '' : 's'} set` };
  if (n === 0) return { enabled: true, label: 'On · no approvers' };
  return { enabled: true, label: `${type.approval_mode} · ${n} approver${n === 1 ? '' : 's'}` };
}

export function FolderApprovalDialog({
  open,
  onOpenChange,
  templateId,
  folderId,
  folderName,
  zoneName,
  docType,
  canEdit,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  templateId: string;
  folderId: string;
  folderName: string;
  zoneName: string;
  docType: DocumentTypeWithApprovers | undefined;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const initialWorkflows = useMemo(
    () => parseWorkflows(docType?.draft_state_workflows_json),
    [docType?.draft_state_workflows_json],
  );

  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<ApprovalMode>('single');
  const [threshold, setThreshold] = useState<number | null>(null);
  const [approvers, setApprovers] = useState<DocumentTypeApproverInput[]>([]);

  useEffect(() => {
    if (!open) return;
    const on = initialWorkflows.includes(ESIGNATURE_WORKFLOW);
    const existing = (docType?.approvers ?? []).map((a) => ({
      role: a.role,
      sequence_order: a.sequence_order,
      signature_meaning: a.signature_meaning,
    }));
    setEnabled(on || existing.length > 0);
    setMode(docType?.approval_mode ?? 'single');
    setThreshold(docType?.any_of_threshold ?? null);
    setApprovers(existing.length > 0 ? existing : [newApproverRow('single', [])]);
  }, [open, docType, initialWorkflows]);

  const save = useMutation({
    mutationFn: async () => {
      const rows = enabled ? approvers : [];
      const effectiveMode: ApprovalMode = rows.length === 0 ? 'parallel' : mode;
      const workflows = new Set(initialWorkflows);
      if (enabled) workflows.add(ESIGNATURE_WORKFLOW);
      else workflows.delete(ESIGNATURE_WORKFLOW);

      const shared = {
        approval_mode: effectiveMode,
        any_of_threshold: effectiveMode === 'parallel' && rows.length > 0 ? threshold : null,
        draft_state_workflows_json: JSON.stringify([...workflows]),
        approvers: rows,
      };

      if (docType) {
        await adminEisf.updateDocumentType(docType.document_type_code, shared);
        return;
      }

      // Place the new type under what already exists: a section named after
      // the zone, else the zone itself (DIA or custom), else create both.
      // Always sending new_zone failed the second folder of a zone with
      // "A custom zone named ... already exists".
      const wanted = zoneName.trim().toLowerCase();
      const { zones } = await adminEisf.getTaxonomy();
      const zone = zones.find((z) => z.zone.trim().toLowerCase() === wanted);
      const section = zone?.sections.find((s) => s.name.trim().toLowerCase() === wanted);
      const placement = section
        ? { parent_section_code: section.code }
        : zone
        ? { new_section: { name: zoneName, parent_zone_name: zone.zone } }
        : { new_zone: { name: zoneName }, new_section: { name: zoneName, parent_zone_name: zoneName } };

      const created = await adminEisf.createDocumentType({
        document_type_name: folderName,
        naming_format_template: `{Study Name} ${folderName}, {Document Date}`,
        definition: '',
        lifecycle: 'Draft to Final',
        who_can_create_json: '[]',
        can_create_site_admin: false,
        can_create_site_staff: false,
        required_fields_json: '[]',
        create_new_draft_versions: 'No',
        steady_state_workflows_json: '[]',
        can_monitors_view: 'No',
        monitor_view_conditions: null,
        site_viewer_can_download: false,
        monitor_can_download: false,
        shareable_via_myveeva: false,
        source_notes: null,
        is_active: true,
        ...placement,
        ...shared,
      });
      await adminBinderTemplates.updateFolder(templateId, folderId, {
        mapped_doc_type_codes: [created.document_type.document_type_code],
      });
    },
    onSuccess: async () => {
      toast.success(`Approvers saved for "${folderName}"`);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['adminEisf', 'documentTypes'] }),
        qc.invalidateQueries({ queryKey: ['adminEisf', 'taxonomy'] }),
        qc.invalidateQueries({ queryKey: ['adminBinderTemplates', 'detail', templateId] }),
      ]);
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Could not save approvers');
    },
  });

  const blocking = enabled && approvers.length === 0;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Approvers"
      description={folderName}
      width={720}
    >
      <div className="space-y-4">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            disabled={!canEdit}
            onChange={(e) => {
              setEnabled(e.target.checked);
              if (e.target.checked && approvers.length === 0) {
                setApprovers([newApproverRow(mode, [])]);
              }
            }}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium text-ink-primary">Approval required</span>
            <span className="block text-xs text-ink-muted">
              Documents filed into this folder must be signed off before they go live.
            </span>
          </span>
        </label>

        {enabled && (
          <div className="space-y-3 rounded border border-surface-border bg-surface-muted/20 p-3">
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-muted">
                How they sign
              </div>
              <div className="flex gap-2">
                {(['single', 'parallel', 'sequential'] as ApprovalMode[]).map((m) => (
                  <label
                    key={m}
                    className={`cursor-pointer rounded border px-3 py-1.5 text-xs ${
                      mode === m
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-surface-border bg-surface-card text-ink-secondary'
                    }`}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      name="folder-approval-mode"
                      checked={mode === m}
                      disabled={!canEdit}
                      onChange={() => {
                        setMode(m);
                        if (m !== 'parallel') setThreshold(null);
                        if (m === 'single' && approvers.length > 1) setApprovers(approvers.slice(0, 1));
                      }}
                    />
                    {m === 'single'
                      ? 'One person'
                      : m === 'parallel'
                      ? 'Everyone, any order'
                      : 'In order'}
                  </label>
                ))}
              </div>
            </div>

            {mode === 'parallel' && approvers.length > 1 && (
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-muted">
                  How many must sign
                </div>
                <input
                  type="number"
                  min={1}
                  max={approvers.length}
                  value={threshold ?? ''}
                  disabled={!canEdit}
                  placeholder={`all ${approvers.length}`}
                  onChange={(e) =>
                    setThreshold(
                      e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10) || 1),
                    )
                  }
                  className="h-8 w-28 rounded border border-surface-border bg-surface-card px-2 text-sm"
                />
                <p className="mt-1 text-xs text-ink-muted">
                  Leave blank to require all {approvers.length}.
                </p>
              </div>
            )}

            <FolderApproverPicker
              approvers={approvers}
              mode={mode}
              disabled={!canEdit}
              onChange={setApprovers}
            />
          </div>
        )}

        {blocking && (
          <div className="flex items-start gap-2 rounded border border-danger-300 bg-danger-50 p-2 text-xs text-danger-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Add at least one approver, or switch approval off.
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-surface-border pt-3">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 rounded border border-surface-border px-3 text-sm text-ink-secondary"
          >
            Cancel
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending || blocking}
              className="btn-primary h-9"
            >
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
